/**
 * SPEC-AgentDetail-Revisions — CxNotifiedButton (renamed from
 * NotifyCustomerToggle).
 *
 * Solid orange when on (with a ✓); outlined orange when off.
 *
 * SPEC-RedDotOps §8.2 — the label comes from notifyLabel(expectedQatt):
 * "CU Notified" on a QATT_1 delivery, "POD Sent" on everything else (also when
 * the QATT is unknown). This supersedes the 2026-05-29 customer-agnostic label.
 *
 * Testid stays `agent-detail-notify-cu-toggle` so the existing
 * unified-qa-detail tests don't break (testid is decoupled from the visual
 * treatment per the SPEC).
 */
import { Starling } from "@/lib/starling-brand";
import { notifyLabel } from "@shared/notify-label";

// card follows the theme (index.css --qa-card); the orange reads on both.
const TOKENS = {
  orange: "#EA580C",
  orangeBorder: "#FDBA74",
  card: "var(--qa-card)",
} as const;

export interface CxNotifiedButtonProps {
  on: boolean;
  customer?: string | null;
  /** qa_deliveries.expected_qatt — 1 → "CU Notified", anything else → "POD Sent". */
  expectedQatt?: number | null;
  onChange: (next: boolean) => void;
  loading?: boolean;
  testId?: string;
}

export function CxNotifiedButton({
  on,
  customer: _customer, // accepted for prop-surface compatibility but unused
  expectedQatt,
  onChange,
  loading,
  testId,
}: CxNotifiedButtonProps) {
  const label = notifyLabel(expectedQatt);
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      disabled={loading}
      data-testid={testId ?? "agent-detail-notify-cu-toggle"}
      style={{
        background: on ? TOKENS.orange : TOKENS.card,
        color: on ? "#fff" : TOKENS.orange,
        border: `1.5px solid ${on ? TOKENS.orange : TOKENS.orangeBorder}`,
        fontFamily: Starling.fontBody,
        fontWeight: 700,
        fontSize: 13,
        padding: "11px 18px",
        borderRadius: 8,
        cursor: loading ? "wait" : "pointer",
        opacity: loading ? 0.6 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
      }}
    >
      {on && <span aria-hidden style={{ fontWeight: 900 }}>✓</span>}
      <span>{label}</span>
    </button>
  );
}

export default CxNotifiedButton;
